import { useCallback, useState } from 'react';

import { unpauseCustomerSubscription } from 'src/graphql/subscription';
import type { ID } from 'src/lib/collection';
import { promisedWait } from 'src/lib/common';
import type { VoidFunction } from 'src/lib/types';
// @ts-expect-error - no TypeScript definitions yet
import { useErrorHandler } from 'src/services/useErrorHandler';
// @ts-expect-error - no TypeScript definitions yet
import { useCustomerActions } from 'src/store/customer';

// The Stripe webhook flips the lifecycle, not the mutation, so an early refresh can still read
// `paused`.
const REFRESH_ATTEMPTS = 3;
const REFRESH_INTERVAL_MS = 1_000;

interface Params {
  customerId?: ID;
}

interface Handlers {
  resuming: boolean;
  handleResume: VoidFunction;
}

export const useHandlers = ({ customerId }: Params): Handlers => {
  const customerActions = useCustomerActions();
  const handleError = useErrorHandler();
  const [resuming, setResuming] = useState(false);

  const handleResume = useCallback(async () => {
    if (!customerId) {
      return;
    }

    setResuming(true);
    try {
      await unpauseCustomerSubscription({ customerId: String(customerId) });

      for (let attempt = 0; attempt < REFRESH_ATTEMPTS; attempt += 1) {
        await promisedWait(REFRESH_INTERVAL_MS);
        await customerActions.refreshCurrentCustomer();
      }
    } catch (error) {
      handleError(error);
    } finally {
      setResuming(false);
    }
  }, [customerId, customerActions, handleError]);

  return { resuming, handleResume };
};
